import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

type TransitionMode = 'enter' | 'exit' | null;

interface ReadingModeContextType {
  triggerTransition: (callback: () => void) => void;
  triggerExitTransition: (callback: () => void) => void;
}

const ReadingModeContext = createContext<ReadingModeContextType>({
  triggerTransition: () => {},
  triggerExitTransition: () => {},
});

export const useReadingMode = () => useContext(ReadingModeContext);

interface ReadingModeProviderProps {
  children: ReactNode;
}

export const ReadingModeProvider = ({ children }: ReadingModeProviderProps) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);
  const [transitionMode, setTransitionMode] = useState<TransitionMode>(null);
  const [phase, setPhase] = useState<'fadeIn' | 'fadeOut'>('fadeIn');

  // Set appropriate background on body during transition to prevent flashes
  useEffect(() => {
    if (isTransitioning) {
      // During exit phase 2 (fading out), use light background
      // Otherwise use dark background
      if (transitionMode === 'exit' && phase === 'fadeOut') {
        document.body.style.backgroundColor = '';
      } else {
        document.body.style.backgroundColor = '#0E0F12';
      }
    }
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [isTransitioning, transitionMode, phase]);

  // Enter reading mode: fade to dark, navigate, then fade out dark overlay
  const triggerTransition = useCallback((callback: () => void) => {
    // Set dark background IMMEDIATELY to prevent any white flash
    document.body.style.backgroundColor = '#0E0F12';
    setTransitionMode('enter');
    setPhase('fadeIn');
    setIsTransitioning(true);
    setPendingCallback(() => callback);
  }, []);

  // Exit reading mode: fade to dark, navigate, then fade out to reveal light page
  const triggerExitTransition = useCallback((callback: () => void) => {
    setTransitionMode('exit');
    setPhase('fadeIn');
    setIsTransitioning(true);
    setPendingCallback(() => callback);
  }, []);

  const handleAnimationComplete = (definition: { opacity?: number }) => {
    if (definition.opacity === 1 && pendingCallback && phase === 'fadeIn') {
      // Short pause in darkness, then navigate
      setTimeout(() => {
        pendingCallback();
        setPendingCallback(null);
        // Wait for page content to fully render BEFORE starting fade out
        setTimeout(() => {
          setPhase('fadeOut');
          // Wait for fade-out animation to complete before cleanup
          setTimeout(() => {
            setIsTransitioning(false);
            setTransitionMode(null);
            setPhase('fadeIn');
          }, 400);
        }, 150);
      }, 120);
    }
  };

  return (
    <ReadingModeContext.Provider value={{ triggerTransition, triggerExitTransition }}>
      {children}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="fixed inset-0 z-[9999] pointer-events-none"
            style={{ 
              backgroundColor: transitionMode === 'exit' && phase === 'fadeOut' 
                ? 'transparent' 
                : '#000000' 
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === 'fadeIn' ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
      </AnimatePresence>
    </ReadingModeContext.Provider>
  );
};
